import { CaretDown, CaretUp, ChevronRight, Download } from '@carbon/icons-react'
import React, { useEffect, useMemo, useState } from 'react'
import {
  downloadSalesforceRolesReport,
  getSalasForceUserPaginatedData,
  getSalesforceRoles
} from '../../api/salesforceAudit/salesforceAudit.service'

import { Button } from '../../components/New/Button/Button'
import { DDSTypography } from 'den-design-system'
import NoRecord from '../../components/common/NoContent/NoRecord'
import NoRecordData from '../../assets/images/setting.svg'
import NoRecordImage from '../../assets/images/folder-not-found 1.svg'
import { SalesforceAuditStore } from '../../store/SalesforceAuditStore'
import Search from '../../components/New/Search/Search'
import Spinner from '../../components/common/Spinner/Spinner'
import Table from '../../components/common/DDS/Table/Table'
import ToastNotification from '../../components/common/DDS/Toast/Toast'
import Tooltip from '../../components/New/Tooltip/Tooltip'
import { convertJsonToCsvDownload } from '../../utils/FileUtils'
import moment from 'moment'
import { observer } from 'mobx-react-lite'
import { paginate } from '../../utils/CommonUtils'
import pallete from '../../global/newPallete'
import { replaceUrl } from '../../utils/UrlUtils'
import routeConstants from '../../constants/RouteConstants'
import { timeFormats } from '../../enum/TIME'
import { timeZone } from '../../utils/DateTimeUtils'
import { useNavigate } from 'react-router-dom'
import { useStoreContext } from '../../store/StoreContext'

const SFRolesOverview: React.FC = () => {
  const store = useStoreContext()
  const navigate = useNavigate()

  const { salesforceAuditStore } = useStoreContext()
  const salesforceAuditId = salesforceAuditStore.auditData?.salesforceAuditId
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [rolesData, setRolesData] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [activeSearchTerm, setActiveSearchTerm] = useState('')
  const [searchColumn, setSearchColumn] = useState('all')
  const [showDropdown, setShowDropdown] = useState(false)

  const [usersWithLicense, setUsersWithLicense] = useState<any[]>([])

  const fetchSalesforcePaginatedUsers = async () => {
    store.uiStore.setGlobalLoader(true)
    setLoading(true)

    try {
      const response: any = await getSalasForceUserPaginatedData({
        $salesforceAuditId: salesforceAuditId as string,
        page: currentPage,
        size: SalesforceAuditStore.userPageSize
      })

      if (response) {
        setUsersWithLicense(response.data)
        salesforceAuditStore.setUserLicensePaginatedData(response.data)
      }
    } catch (err) {
      console.error('Error fetching Salesforce users:', err)
    } finally {
      setLoading(false)
      store.uiStore.setGlobalLoader(false)
    }
  }

  const getUsersAssignedForRole = (roleId: string) => {
    const filtered = usersWithLicense.filter((user: any) => {
      return user?.record?.UserRole?.Id === roleId
    })
    return filtered
  }

  useEffect(() => {
    if (salesforceAuditId) {
      fetchSalesforcePaginatedUsers()
    }
  }, [salesforceAuditId, currentPage])

  const [sortConfig, setSortConfig] = useState<{
    key: string | null
    direction: 'asc' | 'desc' | null
  }>({
    key: null,
    direction: null
  })
  const toggleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return { key: null, direction: null }
    })
  }

  useEffect(() => {
    const fetchRoles = async () => {
      if (!salesforceAuditId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const response = await getSalesforceRoles({ id: salesforceAuditId })

        const roles = response.data.map((item: any) => item.record)
        salesforceAuditStore.setRolesData(response?.data)
        setRolesData(roles)
      } catch (err: any) {
        console.error('Error fetching roles:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchRoles()
  }, [salesforceAuditId])

  const filteredData = useMemo(() => {
    const trimmedSearch = activeSearchTerm
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
    if (!trimmedSearch) return rolesData

    return rolesData.filter((item: any) => {
      const name = (item?.Name || '').toLowerCase()
      const description = (item?.RollupDescription || '').toLowerCase()
      const modifiedBy = item?.LastModifiedBy?.Name?.toLowerCase() || ''
      const usersAssignedCount = getUsersAssignedForRole(item.id)

      // Prepare flexible date formats for LastModifiedDate
      let formattedDates: string[] = []
      if (item?.LastModifiedDate) {
        const date = moment(item.LastModifiedDate)
        formattedDates = [
          date.format('MMMM D YYYY').toLowerCase(),
          date.format('D MMMM YYYY').toLowerCase(),
          date.format('MMM D').toLowerCase(),
          date.format('D MMM').toLowerCase(),
          date.format('MMMM D').toLowerCase(),
          date.format('D MMMM').toLowerCase(),
          date.format('YYYY').toLowerCase(),
          date.format('D').toLowerCase(),
          date.format('MMMM').toLowerCase()
        ]
      }

      const isDateMatch = formattedDates.some((d) => d.includes(trimmedSearch))

      switch (searchColumn) {
        case 'Name':
          return name.includes(trimmedSearch)
        case 'RollupDescription':
          return description.includes(trimmedSearch)
        case 'LastModifiedDate':
          return isDateMatch
        case 'LastModifiedBy':
          return modifiedBy.includes(trimmedSearch)
        case 'Id':
          return usersAssignedCount.includes(trimmedSearch)
        case 'all':
        default:
          return (
            name.includes(trimmedSearch) ||
            description.includes(trimmedSearch) ||
            modifiedBy.includes(trimmedSearch) ||
            usersAssignedCount.includes(trimmedSearch) ||
            isDateMatch
          )
      }
    })
  }, [rolesData, activeSearchTerm, searchColumn, salesforceAuditStore])

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredData
    return [...filteredData].sort((a: any, b: any) => {
      if (sortConfig.key === 'Id') {
        const aCount = getUsersAssignedForRole(a.Id).length
        const bCount = getUsersAssignedForRole(b.Id).length
        return sortConfig.direction === 'asc'
          ? bCount - aCount
          : aCount - bCount
      }
      if (sortConfig.key === 'LastModifiedDate') {
        const dateA = a.LastModifiedDate
          ? new Date(a.LastModifiedDate).getTime()
          : 0
        const dateB = b.LastModifiedDate
          ? new Date(b.LastModifiedDate).getTime()
          : 0
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA
      }
      const valA = a?.[sortConfig.key!] || ''
      const valB = b?.[sortConfig.key!] || ''
      if (!isNaN(Number(valA)) && !isNaN(Number(valB))) {
        return sortConfig.direction === 'asc'
          ? Number(valA) - Number(valB)
          : Number(valB) - Number(valA)
      }
      if (typeof valA === 'string' && typeof valB === 'string') {
        return sortConfig.direction === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA)
      }
      return 0
    })
  }, [filteredData, sortConfig, salesforceAuditStore])

  const paginatedRoleData = paginate(
    sortedData,
    currentPage,
    SalesforceAuditStore.pageSize
  )

  useEffect(() => {
    store.breadcrumbStore.setMultipleBreadcrumbsOptions([
      {
        label: 'Salesforce Audit',
        link:
          replaceUrl(
            routeConstants.SALESFORCE_AUDIT_DASHBOARD,
            store.scopeStore.getScope()['organization-id'] as string,
            store.scopeStore.getScope()['project-id'] as string,
            store.scopeStore.getScope()['environment-id'] as string
          ) + '?page=1&tab=1'
      },
      {
        label: 'Roles',
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_METADATA_OVERVIEW + '/roles',
          store.scopeStore.getScope()['organization-id'] as string,
          store.scopeStore.getScope()['project-id'] as string,
          store.scopeStore.getScope()['environment-id'] as string
        )
      }
    ])
  }, [store.breadcrumbStore.getBreadcrumbsOptions()])

  const downloadRoles = async () => {
    store.uiStore.setGlobalLoader(true)
    setLoading(true)

    const payload = {
      salesforceAuditId: salesforceAuditId as string
    }

    try {
      const response = await downloadSalesforceRolesReport(payload)
      const orgTZ = store.userStore.getOrganizationPreference().timeZone
      const roles: any[] = response.data || []

      if (!roles.length) {
        ToastNotification({
          type: 'error',
          message: 'No roles found for this audit'
        })
        return
      }

      const mapped = roles.map((role) => {
        return {
          Name: role?.name || '-',
          Description: role?.description || '-',
          User_Assigned: role?.userCount || '0',
          LastModifiedDate:
            role?.lastModifiedDate && role?.lastModifiedDate !== '-'
              ? timeZone(
                  orgTZ,
                  role?.lastModifiedDate,
                  timeFormats.fullMonthDateYearTime
                )
              : '-',
          LastModifiedBy: role?.lastModifiedByName || '-'
        }
      })

      const date = new Date()
      const formattedDate = `${(date.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${date
        .getDate()
        .toString()
        .padStart(2, '0')}-${date.getFullYear()}`

      convertJsonToCsvDownload(
        mapped,
        `Salesforce_Roles_Report_${formattedDate}.csv`
      )

      ToastNotification({
        type: 'success',
        message: `Successfully exported ${mapped.length} Roles(s)`
      })
    } catch (error: any) {
      console.error('role Download Error:', error)

      ToastNotification({
        type: 'error',
        message:
          typeof error === 'string'
            ? error
            : error?.message || 'Error downloading Roles report'
      })
    } finally {
      setLoading(false)
      store.uiStore.setGlobalLoader(false)
    }
  }

  const Columns = [
    {
      columnHeaderStyle: { width: '180px' },
      columnDataStyle: { width: '180px' },
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Role Name
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Name')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Name',
      render: (text: any) => (
        <div className='flex justify-start max-w-[180px] text-start row items-start '>
          <div>
            <Tooltip
              id={`tooltip-name-${text}`}
              label={text}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof text === 'string' && text.length > 24 ? text : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate max-w-[180px]'
                >
                  {text || text === 0 ? text : '-'}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        </div>
      )
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Users Assigned
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Id')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Id' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Id' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Id',
      render: (text: any) => {
        return (
          <div className='flex justify-start text-start row items-start'>
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              color={pallete.colors.textDark4}
            >
              {getUsersAssignedForRole(text).length}
            </DDSTypography.Paragraph>
          </div>
        )
      }
    },

    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Role Description
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('RollupDescription')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'RollupDescription' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'RollupDescription' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'RollupDescription',
      render: (text: any) => (
        <div className='flex justify-start max-w-[180px] text-start row items-start '>
          <div>
            <Tooltip
              id={`tooltip-name-${text}`}
              label={text}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof text === 'string' && text.length > 24 ? text : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate max-w-[180px]'
                >
                  {text || text === 0 ? text : '-'}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        </div>
      )
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Last Modified
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('LastModifiedDate')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedDate' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedDate' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'LastModifiedDate',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start'>
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
          >
            {text
              ? moment(text).format(timeFormats.shortMonthDateYearTime)
              : '-'}
          </DDSTypography.Paragraph>
        </div>
      )
    },

    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Modified by
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('LastModifiedBy')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedBy' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedBy' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'LastModifiedBy',
      render: (record: any) => {
        const lastModifiedByName = record?.Name || '-'

        return (
          <div className='flex justify-start text-start items-start'>
            <Tooltip
              id={`tooltip-name-${lastModifiedByName}`}
              label={lastModifiedByName}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof lastModifiedByName === 'string' &&
                lastModifiedByName.length > 16
                  ? ''
                  : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate min-w-[140px] max-w-[140px]'
                >
                  {lastModifiedByName}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        )
      }
    },
    {
      columnHeaderStyle: { width: '20px' },
      columnDataStyle: { width: '20px' },
      heading: '',
      dataLabel: 'Id',
      render: (text: any) => {
        return (
          <div className='cursor-pointer'>
            <ChevronRight
              size={16}
              onClick={() => {
                navigate(text)
              }}
              color={pallete.colors.FillButton}
            />
          </div>
        )
      }
    }
  ]

  return (
    <>
      {loading || store.uiStore.getGlobalLoader() ? (
        <Spinner />
      ) : (
        <div className='flex flex-col w-full py-[24px]'>
          <div className='relative pb-[12px]'>
            <Search
              id='search'
              name='Search'
              placeholder='Search'
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value
                setSearchTerm(value)
                setShowDropdown(value.length > 0)
                if (value.trim() === '') {
                  setActiveSearchTerm('')
                }
              }}
              autoComplete='off'
              width='100%'
              onPressEnter={() => {
                setActiveSearchTerm(searchTerm.trim())
                setCurrentPage(1)
                setShowDropdown(false)
              }}
              onBlur={() => {
                setTimeout(() => {
                  setShowDropdown(false)
                  setCurrentPage(1)
                }, 200)
              }}
            />

            {showDropdown && (
              <div className='absolute z-[99999] mt-[4px] w-full bg-white border border-gray-200 rounded-md shadow-md'>
                {[
                  { label: 'Role Name', value: 'Name' },
                  { label: 'User Assigned', value: 'Id' },
                  { label: 'Description', value: 'Description' },
                  { label: 'Modified by', value: 'LastModifiedBy' },
                  { label: 'Last Modified Date', value: 'LastModifiedDate' },
                  { label: 'All Columns', value: 'all' }
                ].map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      setSearchColumn(option.value)
                      setCurrentPage(1)
                      setActiveSearchTerm(searchTerm)
                      setShowDropdown(false)
                    }}
                    className='px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm text-gray-800'
                  >
                    <span className='font-mulish text-black'>{searchTerm}</span>
                    <span className='ml-[12px] px-[4px] py-[2px] rounded-md bg-gray-100 text-gray-600 text-xs font-medium'>
                      in: {option.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className='w-full flex justify-end mb-[16px]'>
            <Button
              id='outline-button'
              label='Export'
              disabled={
                store.uiStore.getGlobalLoader() || rolesData?.length === 0
              }
              type='neutral'
              size='small'
              onClick={() => {
                downloadRoles()
              }}
              startIcon={<Download color={pallete.colors.neuButtonText} />}
            />
          </div>

          {rolesData?.length > 0 ? (
            <Table
              columns={Columns}
              data={paginatedRoleData}
              totalCount={sortedData?.length}
              currentPage={currentPage}
              rowsPerPage={SalesforceAuditStore.pageSize}
              onPageChange={(page) => setCurrentPage(page)}
              clickable={(id) => {
                navigate(id)
              }}
              clickType='SalesforceRoles'
            />
          ) : searchTerm?.trim() ? (
            <NoRecord
              imageSrc={NoRecordImage}
              style={{ height: '45vh' }}
              text={<div>No Roles found for the given search.</div>}
            />
          ) : (
            <NoRecord
              imageSrc={NoRecordData}
              style={{ height: '45vh' }}
              text={<div>No Roles defined at the moment.</div>}
            />
          )}
        </div>
      )}
    </>
  )
}

export default observer(SFRolesOverview)
